import type { BattleCue } from "../pixi/battleAudio.js";

export type SoundCue = BattleCue | "tap" | "complete";
const KEY = "empire.sound.v1";
let volume = 0.3;
try {
  const saved = localStorage.getItem(KEY);
  if (saved !== null && Number.isFinite(Number(saved)))
    volume = Math.max(0, Math.min(1, Number(saved)));
} catch {
  /* Storage may be unavailable in a private browser. */
}
let context: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;
let voices = 0;
const recent = new Map<SoundCue, number>();
const listeners = new Set<() => void>();

export const soundVolume = (): number => volume;
export function subscribeSound(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
export function setSoundVolume(value: number): void {
  volume = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  try {
    localStorage.setItem(KEY, String(volume));
  } catch {
    /* Optional preference. */
  }
  if (context && master) master.gain.setTargetAtTime(volume * 0.35, context.currentTime, 0.015);
  for (const listener of listeners) listener();
}

/** Called only from a real gesture; failed audio never affects a game action. */
export function unlockSound(): void {
  if (typeof AudioContext === "undefined") return;
  try {
    if (!context) {
      context = new AudioContext();
      master = context.createGain();
      master.gain.value = volume * 0.35;
      const limiter = context.createDynamicsCompressor();
      limiter.threshold.value = -12;
      limiter.ratio.value = 8;
      master.connect(limiter).connect(context.destination);
      noise = context.createBuffer(1, context.sampleRate, context.sampleRate);
      const data = noise.getChannelData(0);
      let seed = 7351;
      for (let i = 0; i < data.length; i++) {
        seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
        data[i] = seed / 2147483648 - 1;
      }
    }
    if (context.state === "suspended") void context.resume().catch(() => undefined);
  } catch {
    /* Audio is an optional presentation layer. */
  }
}

/** Short original synthesized cues, with bounded voices and no loops/network assets. */
export function playSound(cue: SoundCue): void {
  if (
    !context ||
    !master ||
    context.state !== "running" ||
    !volume ||
    document.hidden ||
    voices >= 8
  )
    return;
  const now = context.currentTime;
  if (now - (recent.get(cue) ?? -1) < (cue === "tap" ? 0.04 : 0.09)) return;
  recent.set(cue, now);
  const tone = (from: number, to: number, duration: number, amplitude: number, delay = 0): void => {
    if (voices >= 8) return;
    const oscillator = context!.createOscillator();
    const gain = context!.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(from, now + delay);
    oscillator.frequency.exponentialRampToValueAtTime(to, now + delay + duration);
    gain.gain.setValueAtTime(0, now + delay);
    gain.gain.linearRampToValueAtTime(amplitude, now + delay + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, now + delay + duration);
    oscillator.connect(gain).connect(master!);
    voices++;
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
      voices--;
    };
    oscillator.start(now + delay);
    oscillator.stop(now + delay + duration + 0.02);
  };
  const air = (frequency: number, duration: number, amplitude: number): void => {
    if (voices >= 8) return;
    const source = context!.createBufferSource();
    source.buffer = noise;
    const filter = context!.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = frequency;
    const gain = context!.createGain();
    gain.gain.setValueAtTime(amplitude, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    source.connect(filter).connect(gain).connect(master!);
    voices++;
    source.onended = () => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
      voices--;
    };
    source.start(now);
    source.stop(now + duration + 0.02);
  };
  switch (cue) {
    case "tap":
      tone(620, 440, 0.045, 0.13);
      break;
    case "arrow":
      air(2200, 0.08, 0.12);
      tone(420, 160, 0.06, 0.1);
      break;
    case "ballista":
      air(1200, 0.13, 0.18);
      tone(170, 65, 0.17, 0.2);
      break;
    case "cannon":
      air(600, 0.32, 0.5);
      tone(95, 34, 0.32, 0.42);
      break;
    case "brazier":
      air(1500, 0.35, 0.32);
      break;
    case "frost":
      tone(1200, 600, 0.26, 0.09);
      tone(1800, 900, 0.2, 0.035);
      break;
    case "collapse":
      air(420, 0.45, 0.35);
      tone(80, 28, 0.4, 0.18);
      break;
    case "stoneImpact":
      air(750, 0.1, 0.18);
      tone(125, 48, 0.11, 0.12);
      break;
    case "iceImpact":
      tone(1680, 1350, 0.12, 0.05);
      air(3200, 0.06, 0.04);
      break;
    case "complete":
      tone(523, 523, 0.25, 0.15);
      tone(659, 659, 0.3, 0.12, 0.09);
      tone(784, 784, 0.4, 0.1, 0.18);
      break;
  }
}

export function silenceHiddenSound(): void {
  if (document.hidden && context?.state === "running")
    void context.suspend().catch(() => undefined);
  // Resumption waits for the next gesture; background tabs never start making sound.
}
